import React from 'react';
import styled from 'styled-components';
import { FiDroplet, FiDisc, FiCpu, FiTool, FiSettings, FiZap, FiCircle, FiActivity, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import colors from '../styles/colors';
import usePageMeta from '../hooks/usePageMeta';

const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, ${colors.background.darker} 0%, ${colors.background.medium} 100%);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(135deg, transparent 0%, ${colors.accent.main}08 100%);
    clip-path: polygon(30% 0, 100% 0, 100% 100%, 0% 100%);
  }
`;

const HeroContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Label = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 0.9rem;
  color: ${colors.accent.main};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${colors.text.light};
  line-height: 1.7;
`;

const Section = styled.section`
  padding: 100px 20px;
  background: ${colors.background.card};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
`;

const Card = styled.div`
  background: ${colors.background.medium};
  padding: 35px 30px;
  border-radius: 12px;
  border: 1px solid ${colors.borderLight};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03), 0 4px 20px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${colors.metallic.silver};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 25px 50px rgba(0, 0, 0, 0.4);
    border-color: ${colors.primary.dark};

    &::before {
      transform: scaleX(1);
    }
  }
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  background: ${colors.metallic.darkSteel};
  border: 1px solid ${colors.borderLight};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 2px 8px rgba(0, 0, 0, 0.3);

  svg {
    font-size: 1.6rem;
    color: ${colors.primary.light};
    transition: all 0.3s ease;
  }

  ${Card}:hover & {
    background: ${colors.accent.gradient};
    border-color: ${colors.accent.dark};
    box-shadow: 0 0 20px rgba(0, 102, 177, 0.3);

    svg {
      color: ${colors.text.white};
    }
  }
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  color: ${colors.text.heading};
  margin-bottom: 12px;
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  color: ${colors.text.light};
  line-height: 1.7;
`;

const CTASection = styled.section`
  padding: 80px 20px;
  background: ${colors.background.medium};
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  color: ${colors.text.heading};
  margin-bottom: 15px;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  margin-bottom: 30px;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 18px 40px;
  background: ${colors.accent.gradient};
  color: ${colors.text.white};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  border: 1px solid ${colors.accent.light};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 20px rgba(0, 102, 177, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 8px 30px rgba(0, 102, 177, 0.5);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

const services = [
  { icon: <FiDroplet />, title: 'Oil Changes', desc: 'Quick, quality oil changes using premium synthetic and conventional oils. Keep your engine running smooth.' },
  { icon: <FiDisc />, title: 'Brake Service', desc: 'Complete brake inspection, pad replacement, rotor resurfacing, and brake fluid flush.' },
  { icon: <FiCpu />, title: 'Diagnostics', desc: "We'll figure out what's wrong and explain it in plain English. No confusing jargon." },
  { icon: <FiCircle />, title: 'Tire Services', desc: 'Mount & balance, seasonal changeovers, puncture repair, rotation, new tire sales, and in-house studding.' },
  { icon: <FiTool />, title: 'Light Engine Repair', desc: 'Tune-ups, minor repairs, and engine diagnostics to keep your car running right.' },
  { icon: <FiSettings />, title: 'Engine & Trans Swaps', desc: "Full engine and transmission swaps. We handle the heavy lifting so you don't have to." },
  { icon: <FiZap />, title: 'Electrical & Battery', desc: 'Alternators, starters, wiring repairs, battery testing and replacement. Full electrical system service.' },
  { icon: <FiActivity />, title: 'Suspension', desc: 'Shocks, struts, springs, and alignment. Keep your ride smooth and your handling tight.' },
];

export default function ServicesPage() {
  usePageMeta(
    'Auto Repair Services | Oil Changes, Brakes, Engine Swaps | The Honest Mechanic',
    'Full-service auto repair in Charlotte, VT. Oil changes, brake service, diagnostics, tire services, engine & transmission swaps, electrical, and suspension work.'
  );

  return (
    <PageWrapper>
      <HeroSection>
        <HeroContent>
          <Label>What We Do</Label>
          <Title>Our Services</Title>
          <Subtitle>
            From routine maintenance to complex repairs, we've got you covered with friendly, reliable service.
            No surprises, no upsells — just honest work.
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <Grid>
            {services.map((service, i) => (
              <Card key={i}>
                <IconBox>{service.icon}</IconBox>
                <CardTitle>{service.title}</CardTitle>
                <CardDesc>{service.desc}</CardDesc>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      <CTASection>
        <Container>
          <CTATitle>Ready to Schedule?</CTATitle>
          <CTAText>Give us a call or send a message. We'll get you taken care of.</CTAText>
          <CTAButton to="/contact">
            Contact Us <FiArrowRight />
          </CTAButton>
        </Container>
      </CTASection>
    </PageWrapper>
  );
}
